import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { AuthService } from '@abp/ng.core';
import { appConfig } from 'src/environments/app.config';

export type ClientType = 'agent' | 'customer';

export interface WebSocketMessage {
  type: 'connected' | 'audio_data' | 'start_audio_stream' | 'stop_audio_stream';
  clientId?: ClientType;
  data?: any;
}

@Injectable()
export class WebSocketService {
  isConnected$ = new BehaviorSubject<boolean>(false);
  message$ = new Subject<any>();

  private websocket: WebSocket | null = null;
  private disconnectResolve: (() => void) | null = null;

  constructor(private authService: AuthService) {}

  async connect(callId: string): Promise<void> {
    console.log('WebSocketService: Attempting to connect...');

    return new Promise((resolve, reject) => {
      try {
        if (!this.authService.isAuthenticated) {
          reject(new Error('User is not authenticated. Please login first.'));
          return;
        }

        const accessToken = this.authService.getAccessToken();

        if (!accessToken) {
          reject(new Error('No access token available. Please login again.'));
          return;
        }

        const wsUrl = `${appConfig.chatURL.replace('http', 'ws')}/ws?access_token=${encodeURIComponent(accessToken)}&callId=${encodeURIComponent(callId)}`;

        this.websocket = new WebSocket(wsUrl);

        this.websocket.onopen = () => {
          console.log('WebSocket connected');
          this.isConnected$.next(true);
          resolve();
        };

        this.websocket.onmessage = async (event) => {
          this.message$.next(event.data);
        };

        this.websocket.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.onDisconnected();
          reject(error);
        };

        this.websocket.onclose = () => {
          console.log('WebSocket connection closed');
          this.onDisconnected();
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  sendAudioData(data: ArrayBuffer): void {
    if (this.websocket?.readyState === WebSocket.OPEN) {
      this.websocket.send(data);
    }
  }

  sendTextMessage(message: string): void {
    if (this.websocket?.readyState === WebSocket.OPEN) {
      this.websocket.send(message);
    }
  }

  async disconnect(): Promise<void> {
    if (!this.isConnected$.value && !this.websocket) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      this.disconnectResolve = resolve;

      if (this.websocket) {
        this.websocket.close();
      }
    });
  }

  private onDisconnected(): void {
    this.websocket = null;
    this.isConnected$.next(false);

    if (this.disconnectResolve) {
      this.disconnectResolve();
      this.disconnectResolve = null;
    }
  }
}
